import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Col, Form, Input, Row, Table, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { RevenueCatSubscriptionOut } from '@/api/generated/schemas';
import { BooleanFilter } from '@/components/common/BooleanFilter';
import { TextFilter } from '@/components/common/TextFilter';
import { ShowLink } from '@/components/common/ShowLink';
import { RevenueCatCustomerLink } from '@/components/monetization/RevenueCatLink';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useAdminTable } from '@/hooks/useAdminTable';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

const monospace = { fontFamily: 'monospace' };

const formatDateTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString() : '-';

export const RevenueCatSubscriptionList = () => {
  const t = useTranslate();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<RevenueCatSubscriptionOut>({
      pagination: { pageSize: 20 },
      resource: 'monetization.revenuecat_subscriptions',
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <List canCreate={false}>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t(
                  'monetization.revenuecat_subscriptions.search.placeholder'
                )}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowLink
              resource="monetization.revenuecat_subscriptions"
              id={value}
            />
          )}
        />

        <Table.Column
          dataIndex="subscription_id"
          title={t(
            'monetization.revenuecat_subscriptions.fields.subscription_id'
          )}
          sorter
          sortOrder={getSortOrder(sorters, 'subscription_id')}
          render={(value: string) => <span style={monospace}>{value}</span>}
          filtered={isFieldFiltered(filters as CrudFilter[], 'subscription_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="subscription_id"
              width={220}
              placeholder={t(
                'monetization.revenuecat_subscriptions.filter.subscription_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="account_id"
          title={t('monetization.revenuecat_subscriptions.fields.account_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'account_id')}
          render={(value: number | null) =>
            value != null ? (
              <RevenueCatCustomerLink
                id={value}
                title={t(
                  'monetization.revenuecat_subscriptions.actions.open_in_revenuecat'
                )}
                stopRowClick
              />
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'account_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="account_id"
              width={160}
              placeholder={t(
                'monetization.revenuecat_subscriptions.filter.account_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="customer_id"
          title={t('monetization.revenuecat_subscriptions.fields.customer_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'customer_id')}
          render={(value: number) => (
            <ShowLink resource="monetization.customers" id={value} />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'customer_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="customer_id"
              width={160}
              placeholder={t(
                'monetization.revenuecat_subscriptions.filter.customer_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="product_id"
          title={t('monetization.revenuecat_subscriptions.fields.product_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_id')}
          render={(value: number) => (
            <ShowLink resource="monetization.products" id={value} />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="product_id"
              width={160}
              placeholder={t(
                'monetization.revenuecat_subscriptions.filter.product_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="store_product_identifier"
          title={t(
            'monetization.revenuecat_subscriptions.fields.store_product_identifier'
          )}
          render={(value: string | null | undefined) => value ?? '-'}
        />

        <Table.Column
          dataIndex="store"
          title={t('monetization.revenuecat_subscriptions.fields.store')}
          sorter
          sortOrder={getSortOrder(sorters, 'store')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />

        <Table.Column
          dataIndex="environment"
          title={t('monetization.revenuecat_subscriptions.fields.environment')}
          sorter
          sortOrder={getSortOrder(sorters, 'environment')}
          render={(value: string | null) => {
            if (!value) return '-';
            const color = value === 'PRODUCTION' ? 'success' : 'warning';
            return <Tag color={color}>{value}</Tag>;
          }}
        />

        <Table.Column
          dataIndex="will_renew"
          title={t('monetization.revenuecat_subscriptions.fields.will_renew')}
          sorter
          sortOrder={getSortOrder(sorters, 'will_renew')}
          render={(value: boolean) => (
            <Tag color={value ? 'success' : 'default'}>
              {value ? t('common.yes') : t('common.no')}
            </Tag>
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'will_renew')}
          filterDropdown={({ confirm, clearFilters }) => (
            <BooleanFilter
              field="will_renew"
              placeholder={t(
                'monetization.revenuecat_subscriptions.filter.will_renew'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('monetization.revenuecat_subscriptions.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={formatDateTime}
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('monetization.revenuecat_subscriptions.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          render={formatDateTime}
        />
      </Table>
    </List>
  );
};
